import type { IconName } from '@elara/client-core/ui-contracts/icon-name';

/**
 * The place-classification ruleset (`PlaceCategory`, `classifyPlaceTypes`,
 * `resolvePlaceEventType`) now lives in the shared `@elara/domain` package so the
 * server can denormalize `category`/`eventType` onto the Place doc at write time
 * (see `server/src/places/place-service.ts`). It is re-exported here so existing
 * client imports keep working and the web client retains a fallback during the
 * denormalization migration. Icons/labels below stay client-only presentation.
 */
export type { PlaceCategory } from '@elara/domain/models/place-category';
export { classifyPlaceTypes, resolvePlaceEventType } from '@elara/domain/models/place-category';

/** Map from Google Place primaryType to Material Icon name. */
const TYPE_TO_ICON: Record<string, IconName> = {
  // Automotive
  car_dealer: 'directions_car',
  car_rental: 'car_rental',
  car_repair: 'car_repair',
  car_wash: 'local_car_wash',
  electric_vehicle_charging_station: 'ev_station',
  gas_station: 'local_gas_station',
  parking: 'local_parking',
  parking_garage: 'local_parking',
  parking_lot: 'local_parking',
  rest_stop: 'local_gas_station',

  // Culture
  art_gallery: 'palette',
  art_museum: 'palette',
  art_studio: 'palette',
  auditorium: 'theater_comedy',
  castle: 'castle',
  cultural_landmark: 'account_balance',
  fountain: 'water_drop',
  historical_place: 'account_balance',
  history_museum: 'museum',
  monument: 'account_balance',
  museum: 'museum',
  performing_arts_theater: 'theater_comedy',
  sculpture: 'palette',

  // Education
  library: 'local_library',
  school: 'school',
  university: 'school',
  preschool: 'school',
  primary_school: 'school',
  secondary_school: 'school',

  // Entertainment and Recreation
  amusement_center: 'attractions',
  amusement_park: 'attractions',
  aquarium: 'water',
  bowling_alley: 'sports',
  casino: 'casino',
  comedy_club: 'theater_comedy',
  community_center: 'groups',
  concert_hall: 'music_note',
  convention_center: 'meeting_room',
  cultural_center: 'museum',
  dog_park: 'pets',
  event_venue: 'event',
  ferris_wheel: 'attractions',
  garden: 'yard',
  hiking_area: 'hiking',
  historical_landmark: 'account_balance',
  live_music_venue: 'music_note',
  marina: 'directions_boat',
  miniature_golf_course: 'golf_course',
  movie_theater: 'movie',
  national_park: 'forest',
  night_club: 'nightlife',
  observation_deck: 'visibility',
  opera_house: 'theater_comedy',
  park: 'park',
  picnic_ground: 'deck',
  planetarium: 'public',
  plaza: 'location_city',
  skateboard_park: 'skateboarding',
  state_park: 'forest',
  stadium: 'stadium',
  tourist_attraction: 'attractions',
  video_arcade: 'sports_esports',
  vineyard: 'wine_bar',
  visitor_center: 'info',
  water_park: 'pool',
  wildlife_park: 'forest',
  wildlife_refuge: 'forest',
  zoo: 'pets',

  // Finance
  accounting: 'account_balance',
  atm: 'local_atm',
  bank: 'account_balance',

  // Food and Drink
  acai_shop: 'local_cafe',
  afghani_restaurant: 'restaurant',
  african_restaurant: 'restaurant',
  american_restaurant: 'restaurant',
  argentinian_restaurant: 'restaurant',
  asian_fusion_restaurant: 'restaurant',
  asian_restaurant: 'restaurant',
  australian_restaurant: 'restaurant',
  austrian_restaurant: 'restaurant',
  bagel_shop: 'bakery_dining',
  bakery: 'bakery_dining',
  bangladeshi_restaurant: 'restaurant',
  bar: 'local_bar',
  bar_and_grill: 'local_bar',
  barbecue_restaurant: 'outdoor_grill',
  basque_restaurant: 'restaurant',
  bavarian_restaurant: 'restaurant',
  beer_garden: 'sports_bar',
  belgian_restaurant: 'restaurant',
  bistro: 'restaurant',
  brazilian_restaurant: 'restaurant',
  breakfast_restaurant: 'restaurant',
  brewery: 'sports_bar',
  brewpub: 'sports_bar',
  british_restaurant: 'restaurant',
  brunch_restaurant: 'brunch_dining',
  buffet_restaurant: 'restaurant',
  burmese_restaurant: 'restaurant',
  burrito_restaurant: 'restaurant',
  cafe: 'local_cafe',
  cafeteria: 'restaurant',
  cajun_restaurant: 'restaurant',
  cake_shop: 'cake',
  californian_restaurant: 'restaurant',
  cambodian_restaurant: 'restaurant',
  candy_store: 'storefront',
  cantonese_restaurant: 'restaurant',
  caribbean_restaurant: 'restaurant',
  cat_cafe: 'local_cafe',
  chicken_restaurant: 'restaurant',
  chicken_wings_restaurant: 'restaurant',
  chilean_restaurant: 'restaurant',
  chinese_noodle_restaurant: 'ramen_dining',
  chinese_restaurant: 'restaurant',
  chocolate_factory: 'storefront',
  chocolate_shop: 'storefront',
  cocktail_bar: 'local_bar',
  coffee_roastery: 'local_cafe',
  coffee_shop: 'local_cafe',
  coffee_stand: 'local_cafe',
  colombian_restaurant: 'restaurant',
  confectionery: 'cake',
  croatian_restaurant: 'restaurant',
  cuban_restaurant: 'restaurant',
  czech_restaurant: 'restaurant',
  danish_restaurant: 'restaurant',
  deli: 'restaurant',
  dessert_restaurant: 'cake',
  dessert_shop: 'cake',
  dim_sum_restaurant: 'restaurant',
  diner: 'restaurant',
  dog_cafe: 'local_cafe',
  donut_shop: 'bakery_dining',
  dumpling_restaurant: 'restaurant',
  dutch_restaurant: 'restaurant',
  eastern_european_restaurant: 'restaurant',
  ethiopian_restaurant: 'restaurant',
  european_restaurant: 'restaurant',
  falafel_restaurant: 'restaurant',
  family_restaurant: 'restaurant',
  fast_food_restaurant: 'fastfood',
  filipino_restaurant: 'restaurant',
  fine_dining_restaurant: 'restaurant',
  fish_and_chips_restaurant: 'set_meal',
  fondue_restaurant: 'restaurant',
  food: 'restaurant',
  food_court: 'food_bank',
  french_restaurant: 'restaurant',
  fusion_restaurant: 'restaurant',
  gastropub: 'restaurant',
  german_restaurant: 'restaurant',
  greek_restaurant: 'restaurant',
  gyro_restaurant: 'restaurant',
  halal_restaurant: 'restaurant',
  hamburger_restaurant: 'lunch_dining',
  hawaiian_restaurant: 'restaurant',
  hookah_bar: 'local_bar',
  hot_dog_restaurant: 'lunch_dining',
  hot_dog_stand: 'lunch_dining',
  hot_pot_restaurant: 'restaurant',
  hungarian_restaurant: 'restaurant',
  ice_cream_shop: 'icecream',
  indian_restaurant: 'restaurant',
  indonesian_restaurant: 'restaurant',
  irish_pub: 'sports_bar',
  irish_restaurant: 'restaurant',
  israeli_restaurant: 'restaurant',
  italian_restaurant: 'restaurant',
  japanese_curry_restaurant: 'restaurant',
  japanese_izakaya_restaurant: 'restaurant',
  japanese_restaurant: 'restaurant',
  juice_shop: 'local_cafe',
  kebab_shop: 'restaurant',
  korean_barbecue_restaurant: 'outdoor_grill',
  korean_restaurant: 'restaurant',
  latin_american_restaurant: 'restaurant',
  lebanese_restaurant: 'restaurant',
  lounge_bar: 'local_bar',
  malaysian_restaurant: 'restaurant',
  meal_delivery: 'dinner_dining',
  meal_takeaway: 'takeout_dining',
  mediterranean_restaurant: 'restaurant',
  mexican_restaurant: 'restaurant',
  middle_eastern_restaurant: 'restaurant',
  mongolian_barbecue_restaurant: 'outdoor_grill',
  moroccan_restaurant: 'restaurant',
  noodle_shop: 'ramen_dining',
  north_indian_restaurant: 'restaurant',
  oyster_bar_restaurant: 'set_meal',
  pakistani_restaurant: 'restaurant',
  pastry_shop: 'cake',
  persian_restaurant: 'restaurant',
  peruvian_restaurant: 'restaurant',
  pizza_delivery: 'local_pizza',
  pizza_restaurant: 'local_pizza',
  polish_restaurant: 'restaurant',
  portuguese_restaurant: 'restaurant',
  pub: 'sports_bar',
  ramen_restaurant: 'ramen_dining',
  restaurant: 'restaurant',
  romanian_restaurant: 'restaurant',
  russian_restaurant: 'restaurant',
  salad_shop: 'restaurant',
  sandwich_shop: 'lunch_dining',
  scandinavian_restaurant: 'restaurant',
  seafood_restaurant: 'set_meal',
  shawarma_restaurant: 'restaurant',
  snack_bar: 'fastfood',
  soul_food_restaurant: 'restaurant',
  soup_restaurant: 'ramen_dining',
  south_american_restaurant: 'restaurant',
  south_indian_restaurant: 'restaurant',
  southwestern_us_restaurant: 'restaurant',
  spanish_restaurant: 'restaurant',
  sports_bar: 'sports_bar',
  sri_lankan_restaurant: 'restaurant',
  steak_house: 'restaurant',
  sushi_restaurant: 'restaurant',
  swiss_restaurant: 'restaurant',
  taco_restaurant: 'restaurant',
  taiwanese_restaurant: 'restaurant',
  tapas_restaurant: 'restaurant',
  tea_house: 'local_cafe',
  tex_mex_restaurant: 'restaurant',
  thai_restaurant: 'restaurant',
  tibetan_restaurant: 'restaurant',
  tonkatsu_restaurant: 'restaurant',
  turkish_restaurant: 'restaurant',
  ukrainian_restaurant: 'restaurant',
  vegan_restaurant: 'restaurant',
  vegetarian_restaurant: 'restaurant',
  vietnamese_restaurant: 'restaurant',
  western_restaurant: 'restaurant',
  wine_bar: 'wine_bar',
  winery: 'wine_bar',
  yakiniku_restaurant: 'outdoor_grill',
  yakitori_restaurant: 'restaurant',

  // Government
  city_hall: 'account_balance',
  courthouse: 'gavel',
  embassy: 'account_balance',
  fire_station: 'local_fire_department',
  police: 'local_police',
  post_office: 'local_post_office',

  // Health and Wellness
  chiropractor: 'medical_services',
  dental_clinic: 'dentistry',
  dentist: 'dentistry',
  doctor: 'medical_services',
  drugstore: 'local_pharmacy',
  general_hospital: 'local_hospital',
  hospital: 'local_hospital',
  massage: 'spa',
  massage_spa: 'spa',
  medical_center: 'local_hospital',
  medical_clinic: 'medical_services',
  pharmacy: 'local_pharmacy',
  physiotherapist: 'medical_services',
  sauna: 'spa',
  spa: 'spa',
  wellness_center: 'spa',
  yoga_studio: 'self_improvement',
  beauty_salon: 'content_cut',
  hair_care: 'content_cut',
  hair_salon: 'content_cut',
  barber_shop: 'content_cut',
  gym: 'fitness_center',
  fitness_center: 'fitness_center',
  nail_salon: 'spa',

  // Lodging
  bed_and_breakfast: 'cottage',
  campground: 'camping',
  camping_cabin: 'cabin',
  cottage: 'cottage',
  guest_house: 'cottage',
  hostel: 'hotel',
  hotel: 'hotel',
  inn: 'hotel',
  lodging: 'hotel',
  motel: 'hotel',
  resort_hotel: 'villa',
  rv_park: 'rv_hookup',

  // Natural Features
  beach: 'beach_access',
  island: 'landscape',
  lake: 'water',
  mountain_peak: 'landscape',
  natural_feature: 'landscape',
  nature_preserve: 'forest',
  river: 'water',
  scenic_spot: 'landscape',
  woods: 'forest',

  // Places of Worship
  buddhist_temple: 'temple_buddhist',
  church: 'church',
  hindu_temple: 'temple_hindu',
  mosque: 'mosque',
  shinto_shrine: 'temple_buddhist',
  synagogue: 'synagogue',
  place_of_worship: 'church',

  // Services
  florist: 'local_florist',
  laundry: 'local_laundry_service',
  travel_agency: 'luggage',
  tour_agency: 'tour',
  veterinary_care: 'pets',
  cemetery: 'church',

  // Shopping
  asian_grocery_store: 'grocery',
  bicycle_store: 'pedal_bike',
  book_store: 'menu_book',
  butcher_shop: 'storefront',
  cell_phone_store: 'devices',
  clothing_store: 'checkroom',
  convenience_store: 'local_convenience_store',
  cosmetics_store: 'storefront',
  department_store: 'local_mall',
  discount_store: 'storefront',
  discount_supermarket: 'grocery',
  electronics_store: 'devices',
  farmers_market: 'storefront',
  flea_market: 'storefront',
  furniture_store: 'chair',
  gift_shop: 'redeem',
  grocery_store: 'grocery',
  hardware_store: 'hardware',
  health_food_store: 'grocery',
  home_goods_store: 'home',
  home_improvement_store: 'hardware',
  hypermarket: 'grocery',
  jewelry_store: 'diamond',
  liquor_store: 'liquor',
  market: 'storefront',
  pet_store: 'pets',
  shoe_store: 'steps',
  shopping_mall: 'local_mall',
  sporting_goods_store: 'sports',
  store: 'storefront',
  supermarket: 'grocery',
  thrift_store: 'storefront',
  toy_store: 'toys',
  womens_clothing_store: 'checkroom',

  // Sports
  athletic_field: 'sports',
  golf_course: 'golf_course',
  ice_skating_rink: 'ice_skating',
  playground: 'toys',
  ski_resort: 'downhill_skiing',
  sports_club: 'sports',
  sports_complex: 'sports',
  swimming_pool: 'pool',
  tennis_court: 'sports_tennis',

  // Transportation
  airport: 'flight',
  international_airport: 'flight',
  bus_station: 'directions_bus',
  bus_stop: 'directions_bus',
  ferry_terminal: 'directions_boat',
  ferry_service: 'directions_boat',
  light_rail_station: 'tram',
  subway_station: 'subway',
  taxi_stand: 'local_taxi',
  train_station: 'train',
  tram_stop: 'tram',
  transit_station: 'commute',
};

/**
 * Get the Material Icon name for a Google Places primaryType.
 * Falls back to `'location_on'` for unknown types.
 */
export function getPlaceIcon(primaryType: string | null | undefined): IconName {
  if (!primaryType) return 'location_on';
  return TYPE_TO_ICON[primaryType] ?? 'location_on';
}
